"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  TICK_SPACING,
  calcCounterpartDeposit,
  computeSqrtPriceX96,
  feeToPercent,
  nearestUsableTick,
  priceToTick,
  sqrtPriceToPrice,
} from "@/domain/v3-math";
import type { TokenInfo, V3PoolSelection } from "@/domain/types";
import { V3_FACTORY, V3_POSITION_MANAGER, WDOTO } from "@/data/contracts/addresses";
import { V3_FACTORY_ABI, V3_POOL_ABI, V3_POSITION_MANAGER_ABI } from "@/data/contracts/abis";
import { errorMessage, isAmountOverBalance, safeParseUnits, txUrl } from "@/lib/format";
import { useConnection, usePublicClient, useReadContract, useWalletClient } from "wagmi";
import { useEffect, useRef, useState } from "react";

import type { Address } from "viem";
import { AddToWalletButton } from "@/components/AddToWalletButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PriceRangeChart } from "./PriceRangeChart";
import { SwitchTokensButton } from "@/components/SwitchTokensButton";
import { TokenInputCard } from "@/components/TokenInputCard";
import { TokenSelect } from "@/components/TokenSelect";
import { extractMintedTokenId } from "@/domain/v3-receipt-parsing";
import { useEnsureAllowance } from "@/hooks/useEnsureAllowance";
import { useStatus } from "@/hooks/useStatus";
import { useTokenBalance } from "@/hooks/useBalance";
import { useTokenPair } from "@/hooks/useTokenPair";

// 100 (0.01%) is deliberately excluded — verified on-chain that this
// Factory's feeAmountTickSpacing(100) is unset (returns 0), so no pool can
// ever exist at that tier (createPool requires a non-zero tick spacing).
// Re-add only once the Factory owner actually enables it.
const FEES = [500, 3000, 10000];
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const DEFAULT_A: TokenInfo = { address: WDOTO, symbol: "WDOTO", decimals: 18, name: "Wrapped Doto" };

// Rendered under the mint button once a mint succeeds — not inside the
// status toast, which disappears on its own timer and would take the
// "View transaction" / "Add NFT to wallet" actions with it.
function MintResultLinks({ hash, tokenId }: { hash: string; tokenId: bigint | null }) {
  return (
    <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
      <a href={txUrl(hash)} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-foreground">
        View transaction ↗
      </a>
      {tokenId !== null && (
        <AddToWalletButton
          asset={{ type: "ERC721", address: V3_POSITION_MANAGER, tokenId: tokenId.toString() }}
          assetLabel={`Position #${tokenId}`}
          density="inline"
        />
      )}
    </div>
  );
}

// Simplified relative to dotone-swap-v3's V3Liquidity.tsx (~1350 lines): the
// live on-chain liquidity-depth chart and auto-counterpart-deposit math
// aren't ported yet (data/v3/liquidity-distribution.ts + the hook for it
// already exist and work — see useV3LiquidityDistribution — this component
// just doesn't render them yet). Range/deposit amounts are entered directly.
export function LiquidityWizard({ initialSelection }: { initialSelection: V3PoolSelection | null }) {
  const { address, isConnected } = useConnection();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const ensureAllowance = useEnsureAllowance();
  const { showStatus, showLoading, updateStatus } = useStatus();

  const [step, setStep] = useState<1 | 2>(1);
  const { tokenA, tokenB, setTokenA, setTokenB, swapTokens, setPairDirect } = useTokenPair(DEFAULT_A);
  const [fee, setFee] = useState(3000);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [amountA, setAmountA] = useState("");
  const [amountB, setAmountB] = useState("");
  const [pending, setPending] = useState(false);
  const [mintResult, setMintResult] = useState<{ hash: string; tokenId: bigint | null } | null>(null);

  useEffect(() => {
    if (!initialSelection) return;
    setPairDirect({
      tokenA: initialSelection.token0,
      tokenB: initialSelection.token1,
    });
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFee(initialSelection.fee);
    setStep(2);
    // Only react to a genuinely new selection (nonce changes every click).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelection?.nonce]);

  function switchTokens() {
    swapTokens();
    setAmountA(amountB);
    setAmountB(amountA);
    lastEditedRef.current = lastEditedRef.current === "A" ? "B" : lastEditedRef.current === "B" ? "A" : null;
  }

  const balanceA = useTokenBalance(tokenA);
  const balanceB = useTokenBalance(tokenB);
  const insufficientA = isAmountOverBalance(amountA, tokenA.decimals, balanceA.raw);
  const insufficientB = isAmountOverBalance(amountB, tokenB?.decimals ?? 18, balanceB.raw);

  const sorted = Boolean(tokenB && tokenA.address.toLowerCase() < tokenB.address.toLowerCase());
  const token0 = tokenB ? (sorted ? tokenA : tokenB) : null;
  const token1 = tokenB ? (sorted ? tokenB : tokenA) : null;

  const { data: poolAddress } = useReadContract({
    address: V3_FACTORY,
    abi: V3_FACTORY_ABI,
    functionName: "getPool",
    args: token0 && token1 ? [token0.address, token1.address, fee] : undefined,
    query: { enabled: Boolean(token0 && token1) },
  });
  const poolExists = Boolean(poolAddress && poolAddress !== ZERO_ADDRESS);

  const { data: slot0 } = useReadContract({
    address: poolExists ? (poolAddress as Address) : undefined,
    abi: V3_POOL_ABI,
    functionName: "slot0",
    query: { enabled: poolExists },
  });

  const currentPrice = slot0 && token0 && token1 ? sqrtPriceToPrice(slot0[0], token0.decimals, token1.decimals) : 0;
  const tickSpacing = TICK_SPACING[fee] ?? 60;

  // Seeds a sensible default range as soon as step 2 loads, so the deposit
  // boxes can auto-calculate each other immediately. Without this, minPrice/
  // maxPrice stay empty until the user manually drags the slider or clicks a
  // preset, and calcCounterpartDeposit silently no-ops on an empty range —
  // typing an amount then does nothing to the other box.
  useEffect(() => {
    if (step !== 2 || minPrice || maxPrice) return;
    if (poolAddress === undefined) return; // wait for the pool lookup to settle
    if (poolExists && slot0 === undefined) return; // wait for the live price too, or the range seeds off the pre-pool base=1 fallback
    setRangePreset(10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, poolAddress, poolExists, slot0, currentPrice]);

  function setRangePreset(pct: number | "full") {
    if (pct === "full") {
      setMinPrice("0");
      setMaxPrice("");
      return;
    }
    const base = currentPrice > 0 ? currentPrice : 1;
    setMinPrice((base * (1 - pct / 100)).toFixed(6));
    setMaxPrice((base * (1 + pct / 100)).toFixed(6));
  }

  // Tracks which deposit box the user last typed into, so the price-range
  // effect below knows which side to keep fixed when re-deriving the other.
  const lastEditedRef = useRef<"A" | "B" | null>(null);

  function handleAmountAChange(v: string) {
    setAmountA(v);
    lastEditedRef.current = "A";
    const other = calcCounterpartDeposit({
      changedIsTokenA: true,
      sorted,
      minPrice,
      maxPrice,
      currentPrice,
      value: v,
      otherDecimals: tokenB?.decimals ?? 18,
    });
    if (other !== null) setAmountB(other);
  }

  function handleAmountBChange(v: string) {
    setAmountB(v);
    lastEditedRef.current = "B";
    const other = calcCounterpartDeposit({
      changedIsTokenA: false,
      sorted,
      minPrice,
      maxPrice,
      currentPrice,
      value: v,
      otherDecimals: tokenA.decimals,
    });
    if (other !== null) setAmountA(other);
  }

  // Keeps an already-typed amount's counterpart in sync when the price
  // range or the live pool price changes, instead of letting it go stale.
  useEffect(() => {
    const idx = lastEditedRef.current;
    if (!idx) return;
    const value = idx === "A" ? amountA : amountB;
    if (!value) return;
    const other = calcCounterpartDeposit({
      changedIsTokenA: idx === "A",
      sorted,
      minPrice,
      maxPrice,
      currentPrice,
      value,
      otherDecimals: (idx === "A" ? tokenB?.decimals : tokenA.decimals) ?? 18,
    });
    if (other !== null) (idx === "A" ? setAmountB : setAmountA)(other);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minPrice, maxPrice, currentPrice, sorted]);

  async function mint() {
    if (!walletClient || !address || !publicClient || !token0 || !token1 || !tokenB) {
      showStatus("Select a token pair", "error");
      return;
    }
    const amtA = safeParseUnits(amountA, tokenA.decimals);
    const amtB = safeParseUnits(amountB, tokenB.decimals);
    if (!amtA || !amtB) {
      showStatus("Enter deposit amounts", "error");
      return;
    }
    if (isAmountOverBalance(amountA, tokenA.decimals, balanceA.raw)) {
      showStatus(`Insufficient ${tokenA.symbol} balance`, "error");
      return;
    }
    if (isAmountOverBalance(amountB, tokenB.decimals, balanceB.raw)) {
      showStatus(`Insufficient ${tokenB.symbol} balance`, "error");
      return;
    }
    const minP = Number(minPrice);
    const maxP = maxPrice ? Number(maxPrice) : Number.POSITIVE_INFINITY;
    if (!(minP >= 0) || !(maxP > minP)) {
      showStatus("Enter a valid price range", "error");
      return;
    }
    setPending(true);
    setMintResult(null);
    // One toast, rewritten in place at each step, rather than a new toast
    // per step — so progress reads as a single evolving transaction instead
    // of a stack of unrelated notifications.
    const toastId = showLoading("Preparing transaction…");
    try {
      if (!poolExists) {
        updateStatus(toastId, "Creating pool — confirm in wallet…");
        const initPrice = Number.isFinite(maxP) ? (minP + maxP) / 2 : minP * 2 || 1;
        const sqrtPriceX96 = computeSqrtPriceX96(initPrice);
        const initHash = await walletClient.writeContract({
          address: V3_POSITION_MANAGER,
          abi: V3_POSITION_MANAGER_ABI,
          functionName: "createAndInitializePoolIfNecessary",
          args: [token0.address, token1.address, fee, sqrtPriceX96],
        });
        updateStatus(toastId, "Creating pool — waiting for confirmation…");
        await publicClient.waitForTransactionReceipt({ hash: initHash });
      }

      const rawTickLower = nearestUsableTick(priceToTick(minP, token0.decimals, token1.decimals), tickSpacing);
      const rawTickUpper = Number.isFinite(maxP)
        ? nearestUsableTick(priceToTick(maxP, token0.decimals, token1.decimals), tickSpacing)
        : nearestUsableTick(887272, tickSpacing);
      const tickLower = Math.min(rawTickLower, rawTickUpper);
      const tickUpper = Math.max(rawTickLower, rawTickUpper);

      const amount0Desired = sorted ? amtA : amtB;
      const amount1Desired = sorted ? amtB : amtA;

      updateStatus(toastId, `Checking ${token0.symbol} allowance…`);
      await ensureAllowance(token0.address, V3_POSITION_MANAGER, amount0Desired, address, () =>
        updateStatus(toastId, `Approving ${token0.symbol} — confirm in wallet…`),
      );
      updateStatus(toastId, `Checking ${token1.symbol} allowance…`);
      await ensureAllowance(token1.address, V3_POSITION_MANAGER, amount1Desired, address, () =>
        updateStatus(toastId, `Approving ${token1.symbol} — confirm in wallet…`),
      );

      updateStatus(toastId, "Minting position — confirm in wallet…");
      const deadline = BigInt(Math.floor(Date.now() / 1000) + 1200);
      const hash = await walletClient.writeContract({
        address: V3_POSITION_MANAGER,
        abi: V3_POSITION_MANAGER_ABI,
        functionName: "mint",
        args: [
          {
            token0: token0.address,
            token1: token1.address,
            fee,
            tickLower,
            tickUpper,
            amount0Desired,
            amount1Desired,
            // Hardcoded to 0, matching dotone-swap-v3's documented intentional
            // behavior — avoids double-guarding slippage against the JS-side
            // liquidity approximation.
            amount0Min: 0n,
            amount1Min: 0n,
            recipient: address,
            deadline,
          },
        ],
      });
      updateStatus(toastId, "Minting position — waiting for confirmation…");
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      const tokenId = extractMintedTokenId(receipt.logs, V3_POSITION_MANAGER);
      updateStatus(toastId, tokenId ? `Position #${tokenId} created!` : "Position created!", "success");
      setMintResult({ hash, tokenId });
      setAmountA("");
      setAmountB("");
      balanceA.refetch();
      balanceB.refetch();
    } catch (e) {
      updateStatus(toastId, errorMessage(e), "error");
    } finally {
      setPending(false);
    }
  }

  if (step === 1) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Add liquidity — select pair</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <TokenSelect value={tokenA} onChange={setTokenA} />
            <span className="text-muted-foreground">/</span>
            <TokenSelect value={tokenB} onChange={setTokenB} />
          </div>
          <h3 className="mt-4 text-sm font-medium">Fee tier</h3>
          <p className="text-xs text-muted-foreground">
            The amount earned providing liquidity. Choose an amount that suits your risk tolerance and strategy.
          </p>
          <div className="mt-2 flex gap-2">
            {FEES.map((f) => (
              <Button key={f} type="button" size="sm" variant={fee === f ? "default" : "secondary"} onClick={() => setFee(f)}>
                {feeToPercent(f)}
              </Button>
            ))}
          </div>
          <Button className="mt-4 w-full" disabled={!tokenB} onClick={() => setStep(2)}>
            Continue
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <Button type="button" variant="link" className="h-auto w-fit justify-start p-0 text-xs" onClick={() => setStep(1)}>
          ← Back
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <CardTitle>
            {tokenA.symbol} / {tokenB?.symbol} · {feeToPercent(fee)}
          </CardTitle>
          {!poolExists && (
            <p className="mt-1 text-xs text-[color:var(--color-out-range)]">
              No pool yet for this pair/fee — minting will create it at your range&apos;s midpoint price.
            </p>
          )}
          {poolExists && (
            <p className="mt-1 text-xs text-muted-foreground">
              Current price: 1 {token0?.symbol} = {currentPrice.toFixed(6)} {token1?.symbol}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {[10, 20, 50].map((p) => (
            <Button key={p} type="button" size="sm" variant="secondary" onClick={() => setRangePreset(p)}>
              ±{p}%
            </Button>
          ))}
          <Button type="button" size="sm" variant="secondary" onClick={() => setRangePreset("full")}>
            Full range
          </Button>
        </div>
        <PriceRangeChart
          key={`${token0?.address}-${token1?.address}-${fee}`}
          currentPrice={currentPrice}
          minPrice={minPrice}
          maxPrice={maxPrice}
          onMinChange={setMinPrice}
          onMaxChange={setMaxPrice}
          quoteSymbol={token1?.symbol}
        />
        <div className="grid grid-cols-2 gap-2">
          <Card className="bg-muted">
            <CardContent>
              <div className="text-xs text-muted-foreground">Min price</div>
              <Input
                type="number"
                step="any"
                className="h-auto border-none bg-transparent p-0 text-lg shadow-none focus-visible:ring-0 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="0.0"
              />
            </CardContent>
          </Card>
          <Card className="bg-muted">
            <CardContent>
              <div className="text-xs text-muted-foreground">Max price</div>
              <Input
                type="number"
                step="any"
                className="h-auto border-none bg-transparent p-0 text-lg shadow-none focus-visible:ring-0 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="∞"
              />
            </CardContent>
          </Card>
        </div>
        <TokenInputCard
          label={tokenA.symbol}
          balanceDisplay={balanceA.display}
          balanceLoading={balanceA.loading}
          value={amountA}
          onChange={handleAmountAChange}
          error={insufficientA ? `Insufficient ${tokenA.symbol} balance` : undefined}
        />
        <SwitchTokensButton onClick={switchTokens} disabled={!tokenB} />
        <TokenInputCard
          label={tokenB?.symbol}
          balanceDisplay={balanceB.display}
          balanceLoading={balanceB.loading}
          value={amountB}
          onChange={handleAmountBChange}
          error={insufficientB ? `Insufficient ${tokenB?.symbol} balance` : undefined}
        />
        <Button
          className="w-full"
          disabled={!isConnected || pending || insufficientA || insufficientB}
          onClick={mint}
        >
          {pending
            ? "Minting…"
            : insufficientA
              ? `Insufficient ${tokenA.symbol} balance`
              : insufficientB
                ? `Insufficient ${tokenB?.symbol} balance`
                : poolExists
                  ? "Add liquidity"
                  : "Create pool & add liquidity"}
        </Button>
        {mintResult && <MintResultLinks hash={mintResult.hash} tokenId={mintResult.tokenId} />}
      </CardContent>
    </Card>
  );
}
